import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Animated,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import Highlighter from '@components/HighlightWords';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { Box, Text } from '@components/Restyle';
import { DangerGhostIconButton } from '@components/shared/DangerGhostIconButton';
import Icon from '@components/shared/Icon/Icon';
import {
  ChatDrawerScreen,
  useWebDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import { PopupHiddenSkills } from '@components/Web/PopupHiddenSkills.web';
import {
  useCreateChatMutation,
  useFriendRequestMutation,
  User,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import { useHover } from '@hooks/useHover';
import useMe from '@hooks/useMe';
import { useAppNavigation } from '@navigation/useAppNavigation';
import theme from '@themes/theme';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.colors.grey05,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.colors.grey05,
    width: 132,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.spacing.xs,
  },
}));

export type ProjectMemberType<Roles> = {
  user: User;
  role: Roles;
};

interface ProjectMemberRowProps<Roles> {
  roles?: string[];
  member: ProjectMemberType<Roles>;
  updateRole?: (role: Roles) => Promise<void> | void;
  removeMember?: (member: ProjectMemberType<Roles>) => void;
  filterVal?: string;
  avaterSize?: 'memberList' | 'xs' | 'small' | 'medium' | 'default' | 'large';
  isDropdownOpen?: boolean;
  onDropdownToggle?: (isOpen: boolean) => void;
  /** TA-10502: computed once by the parent from a single listContactUsers query. */
  isContact?: boolean;
}

type MemberDeletedToastProps = {
  message: string;
  duration: number;
};

const MemberDeletedToast: React.FC<MemberDeletedToastProps> = ({
  message,
  duration,
}) => {
  const progressAnim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: 1,
      duration: duration,
      useNativeDriver: false,
    }).start();
  }, [progressAnim, duration]);

  return (
    <View style={styles.toastContainer}>
      <View style={styles.toastContent}>
        <Icon
          marginRight='s'
          name='Selected'
          height={16}
          width={16}
          color='white'
        />
        <Text variant='heading2' color='white'>
          {message}
        </Text>
      </View>
      <Animated.View
        style={[
          styles.progressBar,
          {
            width: progressAnim.interpolate({
              inputRange: [0, 1],
              outputRange: ['0%', '100%'],
            }),
          },
        ]}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  toastContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'black',
    borderRadius: theme.borderRadii.xs,
    position: 'relative',
    overflow: 'hidden',
  },
  toastContent: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 76,
    paddingHorizontal: theme.spacing.m,
  },
  progressBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: 4,
    backgroundColor: theme.colors.greenBrand,
  },
});

export const ProjectMemberRow = <Roles,>({
  member,
  updateRole,
  removeMember,
  filterVal = '',
  roles,
  avaterSize = 'small',
  isDropdownOpen = false,
  onDropdownToggle,
  isContact = false,
}: ProjectMemberRowProps<Roles>) => {
  const { user, role } = member;
  const { name = '', skills, email, phoneNumber, id, avatar } = user;
  const dropdownOpen = isDropdownOpen;
  const setDropdownOpen = (open: boolean) => {
    if (onDropdownToggle) {
      onDropdownToggle(open);
    }
  };
  const [isDeleteModalVisible, setDeleteModalVisible] =
    useState<boolean>(false);
  const [showAdminConfirmModal, setShowAdminConfirmModal] = useState(false);
  const [pendingAdminRole, setPendingAdminRole] = useState<Roles | null>(null);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const memberFullName = member.user.firstName + ' ' + member.user.lastName;
  const { t } = useTranslation('models');
  const { t: f } = useTranslation('format');
  const toast = useToast();
  const [skillWidth, setSkillWidth] = useState(0);
  const [skillWidth1, setSkillWidth1] = useState(0);
  const [skillWidth2, setSkillWidth2] = useState(0);
  const [skillWidth3, setSkillWidth3] = useState(0);
  const [skillWidth4, setSkillWidth4] = useState(0);
  const [skillWidth5, setSkillWidth5] = useState(0);
  const [skillWidth6, setSkillWidth6] = useState(0);
  const [skillWidth7, setSkillWidth7] = useState(0);
  const [skillWidth8, setSkillWidth8] = useState(0);
  const [skillWidth9, setSkillWidth9] = useState(0);
  const [skillWidth10, setSkillWidth10] = useState(0);
  const [skillWidth100, setSkillWidth100] = useState(0);
  const navigation = useAppNavigation();
  const {
    isTaskWebPanelOpen,
    setIsTaskWebPanelOpen,
    chatListOpen,
    chatExpanded,
    setChatListOpen,
    setShouldChatListOpen,
  } = useWebDrawer();
  const { me } = useMe();
  const { setActiveMessageId, setIsOpenChat } = useActiveChat();
  // Member-row hover (TA-9005): grey01; controls inside the row (role
  // dropdown, chat icon) deepen one step to grey02 on their own hover.
  const { isHovered, hoverProps } = useHover();
  const { isHovered: isRoleHovered, hoverProps: roleHoverProps } = useHover({
    disabled: !updateRole,
  });
  const { isHovered: isChatHovered, hoverProps: chatHoverProps } = useHover();

  const [createChat, { data: createChatData }] = useCreateChatMutation();

  useEffect(() => {
    if (!createChatData) return;
    if (!chatExpanded && chatListOpen) {
      setShouldChatListOpen(true);
      setChatListOpen(false);
    }
    setIsOpenChat(true);
    setActiveMessageId?.(undefined);
    navigation.navigateToChatDrawer({
      screen: ChatDrawerScreen.details,
      activeChat: createChatData.createChat,
    });
  }, [createChatData]);

  const [friendRequest, { data: friendRequestData }] = useFriendRequestMutation(
    {
      refetchQueries: ['listUsers', 'listUsersPagination', 'listContactUsers'],
    }
  );

  useEffect(() => {
    if (!friendRequestData) return;
    toast?.show(t('chat.connectionRequestSend'), {
      type: 'normal',
      duration: 3000,
      placement: 'bottom',
    });
  }, [friendRequestData]);

  const handleChatPress = () => {
    if (isContact) {
      createChat({
        variables: {
          attributes: {
            userIds: [id],
          },
        },
      });
    } else {
      friendRequest({
        variables: {
          attributes: {
            userIds: [id],
          },
        },
      });
    }
  };

  const isCurrentUser = me?.id === id;

  const renderSkills = (): React.ReactNode => {
    if (!skills || skills.length === 0 || skillWidth <= 0) return null;

    const getSkillsWidth = (index: number) => {
      switch (index) {
        case 0:
          return theme.spacing.xxs;
        case 1:
          return skillWidth1 + theme.spacing.xxs;
        case 2:
          return skillWidth1 + skillWidth2 + theme.spacing.xxs * index;
        case 3:
          return (
            skillWidth1 + skillWidth2 + skillWidth3 + theme.spacing.xxs * index
          );
        case 4:
          return (
            skillWidth1 +
            skillWidth2 +
            skillWidth3 +
            skillWidth4 +
            theme.spacing.xxs * index
          );
        case 5:
          return (
            skillWidth1 +
            skillWidth2 +
            skillWidth3 +
            skillWidth4 +
            skillWidth5 +
            theme.spacing.xxs * index
          );
        case 6:
          return (
            skillWidth1 +
            skillWidth2 +
            skillWidth3 +
            skillWidth4 +
            skillWidth5 +
            skillWidth6 +
            theme.spacing.xxs * index
          );
        case 7:
          return (
            skillWidth1 +
            skillWidth2 +
            skillWidth3 +
            skillWidth4 +
            skillWidth5 +
            skillWidth6 +
            skillWidth7 +
            theme.spacing.xxs * index
          );
        case 8:
          return (
            skillWidth1 +
            skillWidth2 +
            skillWidth3 +
            skillWidth4 +
            skillWidth5 +
            skillWidth6 +
            skillWidth7 +
            skillWidth8 +
            theme.spacing.xxs * index
          );
        case 9:
          return (
            skillWidth1 +
            skillWidth2 +
            skillWidth3 +
            skillWidth4 +
            skillWidth5 +
            skillWidth6 +
            skillWidth7 +
            skillWidth8 +
            skillWidth9 +
            theme.spacing.xxs * index
          );
        case 10:
          return (
            skillWidth1 +
            skillWidth2 +
            skillWidth3 +
            skillWidth4 +
            skillWidth5 +
            skillWidth6 +
            skillWidth7 +
            skillWidth8 +
            skillWidth9 +
            skillWidth10 +
            theme.spacing.xxs * index
          );
        default:
          return 0;
      }
    };

    const getSkillMaxWidth = (index: number) => {
      return skills.length === index + 1
        ? getSkillMaxWidthNotHaveEllipsis(index)
        : getSkillMaxWidthHaveEllipsis(index);
    };

    const getSkillMaxWidthNotHaveEllipsis = (index: number) => {
      return (
        skillWidth - getSkillsWidth(index) + (index > 0 ? theme.spacing.xxs : 0)
      );
    };

    const getSkillMaxWidthHaveEllipsis = (index: number) => {
      return (
        skillWidth - getSkillsWidth(index) - skillWidth100 - theme.spacing.xs
      );
    };

    const getSkillBox = (index: number, needSetWidth: boolean) => {
      let setSkillWidthFn: (value: React.SetStateAction<number>) => void;
      switch (index) {
        case 0:
          setSkillWidthFn = setSkillWidth1;
          break;
        case 1:
          setSkillWidthFn = setSkillWidth2;
          break;
        case 2:
          setSkillWidthFn = setSkillWidth3;
          break;
        case 3:
          setSkillWidthFn = setSkillWidth4;
          break;
        case 4:
          setSkillWidthFn = setSkillWidth5;
          break;
        case 5:
          setSkillWidthFn = setSkillWidth6;
          break;
        case 6:
          setSkillWidthFn = setSkillWidth7;
          break;
        case 7:
          setSkillWidthFn = setSkillWidth8;
          break;
        case 8:
          setSkillWidthFn = setSkillWidth9;
          break;
        case 9:
          setSkillWidthFn = setSkillWidth10;
          break;
      }
      return (
        <Box
          key={id.toString() + '_' + skills[index].id}
          marginTop='xxs'
          marginRight='xxs'
          padding='xxs'
          borderRadius='xxs'
          backgroundColor='grey01'
          maxWidth={getSkillMaxWidth(index)}
          onLayout={(e) =>
            needSetWidth &&
            setSkillWidthFn &&
            setSkillWidthFn(e.nativeEvent.layout.width)
          }>
          <Text
            variant='bodyTaskName'
            color='textPrimary'
            numberOfLines={1}
            style={{
              overflow: 'hidden',
              textOverflow: 'clip',
            }}>
            {skills[index].name}
          </Text>
        </Box>
      );
    };

    const getSkillsBox = (
      skillsNum: number,
      haveEllipsis: boolean,
      needSetWidth: boolean
    ) => {
      const newSkills = [];
      for (let i = 0; i < skillsNum; i++) {
        newSkills.push(getSkillBox(i, needSetWidth));
      }

      const skill100Box = haveEllipsis ? (
        <BootstrapTooltip
          key={id + '_' + skills[skillsNum].id}
          title={
            <PopupHiddenSkills
              showHiddenSkillsFirstIndex={skillsNum}
              skills={skills}
            />
          }>
          <Box
            zIndex={10}
            marginTop='xxs'
            marginRight='xxs'
            padding='xxs'
            borderRadius='xxs'
            backgroundColor='grey01'
            onLayout={(e) => {
              setSkillWidth100(e.nativeEvent.layout.width);
            }}>
            <Text variant='bodyTaskName' color='textPrimary' numberOfLines={1}>
              ...
            </Text>
          </Box>
        </BootstrapTooltip>
      ) : undefined;

      return [newSkills, skill100Box];
    };

    for (let i = 1; i <= skills.length; i++) {
      if (getSkillMaxWidthNotHaveEllipsis(i) > skillWidth100 * 2) {
        if (skills.length > i) {
          if (getSkillMaxWidthHaveEllipsis(i) <= theme.spacing.xxs) {
            return getSkillsBox(i, true, false);
          } else {
            continue;
          }
        }
        return getSkillsBox(i, false, true);
      }
      return getSkillsBox(i - 1 > 0 ? i - 1 : 1, skills.length > 1, true);
    }

    return null;
  };

  return (
    <Box>
      {isDeleteModalVisible && (
        <ConfirmModal
          showModal={isDeleteModalVisible}
          isAlert={true}
          padding='l'
          onClose={() => setDeleteModalVisible(false)}
          onPress={() => {
            removeMember && removeMember(member);
            setDeleteModalVisible(false);

            toast?.show(
              <MemberDeletedToast
                message={t('teams.removeTeamMember.memberDeleted')}
                duration={3000}
              />,
              {
                type: 'custom',
                duration: 3000,
                placement: 'bottom',
                animationType: 'slide-in',
                style: {
                  marginBottom: 39,
                  backgroundColor: 'transparent',
                  shadowColor: 'transparent',
                },
              }
            );
          }}
          buttonText={t('teams.removeTeamMember.remove')}
          title={t('teams.removeTeamMember.title')}
          message={
            <Text>
              <Text variant='labelEmphasized'>{memberFullName}</Text>{' '}
              {t('teams.removeTeamMember.removeProjectMember')}
            </Text>
          }
        />
      )}
      {showAdminConfirmModal && (
        <ConfirmModal
          showModal={showAdminConfirmModal}
          isAlert={false}
          padding='l'
          onClose={() => {
            setShowAdminConfirmModal(false);
            setPendingAdminRole(null);
          }}
          onPress={async () => {
            if (pendingAdminRole && updateRole) {
              setIsUpdatingRole(true);
              try {
                await updateRole(pendingAdminRole);
              } finally {
                setIsUpdatingRole(false);
              }
            }
            setShowAdminConfirmModal(false);
            setPendingAdminRole(null);
          }}
          buttonText={t('projects.members.confirmAdmin')}
          title={t('projects.members.changeToAdmin')}
          message={
            <Text>
              {t('projects.members.changeToAdminMessage', {
                name: memberFullName,
              })}
            </Text>
          }
        />
      )}
      <Box
        {...hoverProps}
        flexDirection='row'
        alignItems='center'
        backgroundColor={isHovered ? 'grey01' : undefined}>
        <Box flex={1.2} flexDirection='row' alignItems='center'>
          <Avatar
            id={id}
            avatar={avatar}
            label={name}
            size={avaterSize}
            onPress={() => {
              isTaskWebPanelOpen && setIsTaskWebPanelOpen(false);
              navigation.navigateToContact(user);
            }}
          />
          <Text
            accessibilityLabel='Project Member'
            variant='bodyTaskName'
            marginLeft='xs'
            style={{ flex: 1 }}
            numberOfLines={1}>
            <Highlighter
              autoEscape
              highlightStyle={{
                color: theme.colors.greenSecondary,
                backgroundColor: theme.colors.background,
              }}
              searchWords={filterVal?.split(' ') || ''}
              textToHighlight={name}
            />
          </Text>
        </Box>
        <Box
          flex={1}
          flexDirection='row'
          justifyContent='flex-start'
          flexWrap='wrap'
          onLayout={(e) => setSkillWidth(e.nativeEvent.layout.width)}>
          {renderSkills()}
        </Box>
        <Box flex={1.2} flexDirection='row' justifyContent='flex-start'>
          <Text variant='metadata1' color='textPrimary' numberOfLines={1}>
            {email}
          </Text>
        </Box>
        <Box flex={0.8} flexDirection='row' justifyContent='flex-start'>
          <Text variant='metadata1' color='textPrimary' numberOfLines={1}>
            {f('phoneNumber', {
              val: phoneNumber,
            })}
          </Text>
        </Box>
        <View
          style={{
            flex: 0.5,
            flexDirection: 'row',
            alignItems: 'center',
            paddingLeft: theme.spacing.s,
            minWidth: 0,
          }}>
          <div
            ref={anchorRef}
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
            }}>
            <TouchableOpacity
              {...roleHoverProps}
              onPress={() => updateRole && setDropdownOpen(!dropdownOpen)}
              disabled={!updateRole}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingHorizontal: theme.spacing.m,
                paddingVertical: theme.spacing.xs,
                borderRadius: theme.borderRadii.xs,
                backgroundColor: isRoleHovered
                  ? theme.colors.grey02
                  : 'transparent',
              }}
              accessibilityLabel='RoleDropDown'>
              {role && (
                <Text
                  accessibilityLabel='Member role'
                  variant='webSmall'
                  color='textPrimary'
                  style={{ textTransform: 'capitalize', minWidth: 50 }}>
                  {String(role).toLowerCase()}
                </Text>
              )}
              {updateRole &&
                (isUpdatingRole ? (
                  <ActivityIndicator
                    size={14}
                    color={theme.colors.textPrimary}
                    style={{ marginLeft: theme.spacing.xxs }}
                  />
                ) : (
                  <Icon
                    name='ChevronDown'
                    color='textPrimary'
                    marginLeft='xxs'
                  />
                ))}
            </TouchableOpacity>
          </div>
          <Menu
            anchorEl={anchorRef.current}
            open={dropdownOpen && !!roles}
            onClose={() => setDropdownOpen(false)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 120,
                  borderRadius: `${theme.borderRadii.s}px`,
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                },
              },
            }}>
            {roles?.map((i) => (
              <MenuItem
                key={i}
                onClick={() => {
                  if (
                    String(i).toLowerCase() === 'admin' &&
                    String(role).toLowerCase() !== 'admin'
                  ) {
                    setPendingAdminRole(i as Roles);
                    setShowAdminConfirmModal(true);
                    setDropdownOpen(false);
                    return;
                  }
                  if (updateRole) {
                    setIsUpdatingRole(true);
                    Promise.resolve(updateRole(i as Roles)).finally(() =>
                      setIsUpdatingRole(false)
                    );
                  }
                  setDropdownOpen(false);
                }}
                sx={{
                  textTransform: 'capitalize',
                  fontSize: 14,
                  py: 1,
                  px: 1,
                  '&:hover': {
                    backgroundColor: theme.colors.grey01,
                  },
                }}>
                <Text variant='webBodySecondary' color='textPrimary'>
                  {String(i).toLowerCase()}
                </Text>
              </MenuItem>
            ))}
          </Menu>
        </View>
        <Box width={40} alignItems='center' justifyContent='center'>
          {!isCurrentUser && (
            <TooltipWebV3
              title={
                <Text variant='labelSmall' color='white'>
                  Send Message
                </Text>
              }
              placement='bottom-end'
              variant='dark'>
              <Box>
                <TouchableOpacity
                  {...chatHoverProps}
                  onPress={handleChatPress}
                  accessibilityLabel='Start chat with member'>
                  <Box
                    padding='xs'
                    borderRadius='xs'
                    alignItems='center'
                    justifyContent='center'
                    backgroundColor={isChatHovered ? 'grey02' : undefined}>
                    <Icon
                      name='MessageSquare'
                      width={16}
                      height={16}
                      color='black'
                    />
                  </Box>
                </TouchableOpacity>
              </Box>
            </TooltipWebV3>
          )}
        </Box>
        <Box width={40} alignItems='center' justifyContent='center'>
          {removeMember && !isCurrentUser && (
            <TooltipWebV3
              title={
                <Text variant='labelSmall' color='white'>
                  {t('teams.removeTeamMember.title')}
                </Text>
              }
              placement='bottom-end'
              variant='dark'>
              <Box>
                <DangerGhostIconButton
                  onPress={() => setDeleteModalVisible(true)}
                  accessibilityLabel={t('editMembers.removeMember.removeTitle')}
                />
              </Box>
            </TooltipWebV3>
          )}
        </Box>
      </Box>
    </Box>
  );
};
